import { PaymentLinkBuilder } from "@/components/PaymentLinkBuilder";
import {
  ArrowRight,
  BarChart3,
  FileText,
  Globe,
  Lock,
  Share2,
  Zap,
} from "lucide-react";
import { useState } from "react";

export default function HomePage() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 10 use cases for structured DollarBack links
  const useCases = [
    {
      title: "Coffee with Friends ☕",
      description:
        'Create a "buy me a coffee" link that you can share whenever someone offers to treat you.',
      icon: "☕",
    },
    {
      title: "Splitting Dinner Bills 🍽️",
      description:
        "Share your customized DollarBack link after dinner to collect your share without the awkward calculator math.",
      icon: "🍽️",
    },
    {
      title: "Group Gift Collections 🎁",
      description:
        "Collect contributions for a friend's birthday gift with a descriptive DollarBack link that explains what it's for.",
      icon: "🎁",
    },
    {
      title: "Trip Planning with Friends 🏝️",
      description:
        "Create labeled DollarBack links for expenses like 'Cabin Deposit', 'Gas Money', or 'Activity Fees' when organizing trips.",
      icon: "🏝️",
    },
    {
      title: "Roommate Expenses 🏠",
      description:
        "Share consistent, labeled DollarBack links for rent, utilities, and household expenses with your roommates.",
      icon: "🏠",
    },
    {
      title: "Concert & Event Tickets 🎟️",
      description:
        "When purchasing tickets for friends, send structured DollarBack links clearly showing the event details.",
      icon: "🎟️",
    },
    {
      title: "Fantasy Sports Leagues 🏈",
      description:
        "Collect league dues with custom DollarBack links that specify the season and league name for clarity.",
      icon: "🏈",
    },
    {
      title: "Fundraising for Personal Causes 💝",
      description:
        "Create meaningful DollarBack links when raising money for a friend in need or personal cause.",
      icon: "💝",
    },
    {
      title: "Family Subscription Sharing 📱",
      description:
        "Collect your family members' portions of shared subscriptions with clear, itemized DollarBack links.",
      icon: "📱",
    },
    {
      title: "Ride Sharing Costs 🚗",
      description:
        "Request gas money or shared rideshare costs with structured DollarBack links showing the destination and amount.",
      icon: "🚗",
    },
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <header className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-purple-600 via-indigo-500 to-blue-400" />
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-14 sm:pb-32 lg:px-8 lg:py-40">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="mt-10 text-4xl font-bold tracking-tight text-white sm:text-6xl">
              Get Paid Faster with{" "}
              <span className="text-blue-300">DollarBack</span> 💸
            </h1>
            <p className="mt-6 text-lg leading-8 text-blue-100">
              No more "Hey, can everyone Venmo me $23 for pizza?" with crickets
              in response. DollarBack creates structured payment links that make
              it effortless for friends to pay you right away. 💰
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all duration-300"
              >
                Create a DollarBack Link
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Use Cases Carousel */}
      <div className="bg-indigo-50 py-24 sm:py-32 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600">
              Use Cases 👥
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              How can DollarBack help you? 🤔
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              DollarBack is designed for anyone who needs to collect payments
              quickly and easily.
            </p>
          </div>

          {/* Multi-row scrolling cards */}
          <div className="mt-16 sm:mt-20 lg:mt-24 relative overflow-hidden">
            {/* First row - continuous scroll left to right */}
            <div className="mb-8 relative">
              <div className="animate-scroll-left flex space-x-4">
                {/* First set of cards */}
                {useCases.slice(0, 5).map((useCase, index) => (
                  <div
                    key={`row1-set1-${index}`}
                    className="w-64 md:w-72 flex-shrink-0"
                  >
                    <div className="h-full bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden p-6 border border-indigo-100">
                      <div className="flex justify-center items-center w-12 h-12 rounded-full bg-indigo-100 text-2xl mb-4">
                        {useCase.icon}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {useCase.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                ))}
                {/* Duplicate first set for seamless loop */}
                {useCases.slice(0, 5).map((useCase, index) => (
                  <div
                    key={`row1-set2-${index}`}
                    className="w-64 md:w-72 flex-shrink-0"
                  >
                    <div className="h-full bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden p-6 border border-indigo-100">
                      <div className="flex justify-center items-center w-12 h-12 rounded-full bg-indigo-100 text-2xl mb-4">
                        {useCase.icon}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {useCase.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Second row - continuous scroll right to left (reversed) */}
            <div className="mb-8 relative">
              <div className="animate-scroll-right flex space-x-4">
                {/* First set of cards */}
                {useCases.slice(3, 8).map((useCase, index) => (
                  <div
                    key={`row2-set1-${index}`}
                    className="w-64 md:w-72 flex-shrink-0"
                  >
                    <div className="h-full bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden p-6 border border-indigo-100">
                      <div className="flex justify-center items-center w-12 h-12 rounded-full bg-indigo-100 text-2xl mb-4">
                        {useCase.icon}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {useCase.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                ))}
                {/* Duplicate for seamless loop */}
                {useCases.slice(3, 8).map((useCase, index) => (
                  <div
                    key={`row2-set2-${index}`}
                    className="w-64 md:w-72 flex-shrink-0"
                  >
                    <div className="h-full bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden p-6 border border-indigo-100">
                      <div className="flex justify-center items-center w-12 h-12 rounded-full bg-indigo-100 text-2xl mb-4">
                        {useCase.icon}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {useCase.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {useCase.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Third row - continuous scroll left to right (like first row but with different speed) */}
            <div className="relative">
              <div className="animate-scroll-left-slow flex space-x-4">
                {/* First set of cards */}
                {useCases
                  .slice(5, 10)
                  .concat(useCases.slice(0, 2))
                  .map((useCase, index) => (
                    <div
                      key={`row3-set1-${index}`}
                      className="w-64 md:w-72 flex-shrink-0"
                    >
                      <div className="h-full bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden p-6 border border-indigo-100">
                        <div className="flex justify-center items-center w-12 h-12 rounded-full bg-indigo-100 text-2xl mb-4">
                          {useCase.icon}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {useCase.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {useCase.description}
                        </p>
                      </div>
                    </div>
                  ))}
                {/* Duplicate for seamless loop */}
                {useCases
                  .slice(5, 10)
                  .concat(useCases.slice(0, 2))
                  .map((useCase, index) => (
                    <div
                      key={`row3-set2-${index}`}
                      className="w-64 md:w-72 flex-shrink-0"
                    >
                      <div className="h-full bg-white rounded-2xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden p-6 border border-indigo-100">
                        <div className="flex justify-center items-center w-12 h-12 rounded-full bg-indigo-100 text-2xl mb-4">
                          {useCase.icon}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {useCase.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {useCase.description}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Gradient overlays to fade the edges */}
            <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-indigo-50 to-transparent z-10"></div>
            <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-indigo-50 to-transparent z-10"></div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600">
              Get Paid Easily 💯
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Streamlined payment links that work for you 🤑
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              DollarBack creates custom payment links with your preferred
              payment apps - you focus on your activity, we'll handle the link
              logistics.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl">
            <dl className="grid max-w-xl grid-cols-1 gap-y-10 gap-x-8 lg:max-w-none lg:grid-cols-2 lg:gap-y-16">
              <div className="relative pl-16">
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                    <Zap className="h-6 w-6 text-white" />
                  </div>
                  Custom Payment Links 🔗
                </dt>
                <dd className="mt-2 text-base leading-7 text-gray-600">
                  Create payment links with specific amounts and memos in
                  seconds. Recipients instantly know what they're paying for -
                  no confusion, no delays.
                </dd>
              </div>
              <div className="relative pl-16">
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                    <BarChart3 className="h-6 w-6 text-white" />
                  </div>
                  Link Analytics 📊
                </dt>
                <dd className="mt-2 text-base leading-7 text-gray-600">
                  Track visits to your payment links with detailed analytics.
                  See who clicked, when they visited, and optimize your payment
                  collection strategy.
                </dd>
              </div>
              <div className="relative pl-16">
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                    <Globe className="h-6 w-6 text-white" />
                  </div>
                  Payment App Integration 🔄
                </dt>
                <dd className="mt-2 text-base leading-7 text-gray-600">
                  We integrate with the apps you already use - Venmo, PayPal,
                  and Cash App - redirecting users to pay you directly through
                  their preferred platform.
                </dd>
              </div>
              <div className="relative pl-16">
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                    <Lock className="h-6 w-6 text-white" />
                  </div>
                  Privacy-Focused 🔒
                </dt>
                <dd className="mt-2 text-base leading-7 text-gray-600">
                  We never handle or store payment information. DollarBack
                  simply creates and tracks structured payment links that
                  redirect to your existing payment apps.
                </dd>
              </div>
              <div className="relative pl-16">
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                    <Share2 className="h-6 w-6 text-white" />
                  </div>
                  One-Click Sharing 📱
                </dt>
                <dd className="mt-2 text-base leading-7 text-gray-600">
                  Share your payment links instantly via text, email, or social
                  media. Recipients simply click and pay - no apps to download,
                  no accounts to create.
                </dd>
              </div>
              <div className="relative pl-16">
                <dt className="text-base font-semibold leading-7 text-gray-900">
                  <div className="absolute top-0 left-0 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  Clear Payment Context 📝
                </dt>
                <dd className="mt-2 text-base leading-7 text-gray-600">
                  No more vague payment requests. Each link includes a
                  pre-filled memo and amount, eliminating confusion about what
                  the payment is for and how much to send.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-500 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Ready to start getting paid back?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-indigo-100">
            Create your first payment link in seconds. No signup required.
          </p>
          <div className="mt-10">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center justify-center rounded-md bg-white px-5 py-3 text-base font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all duration-300"
            >
              Create a DollarBack Link
              <ArrowRight className="ml-2 -mr-0.5 h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-indigo-900">
        <div className="mx-auto max-w-7xl px-6 py-12 flex items-center justify-center lg:px-8">
          <p className="text-center text-xs leading-5 text-indigo-200">
            &copy; {new Date().getFullYear()} Jab Labs
          </p>
        </div>
      </footer>

      {/* Added LinkGenerator Modal */}
      <PaymentLinkBuilder
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        // Optional: Add onSuccess handler if needed
        // onSuccess={() => console.log("Link action successful!")}
      />
    </div>
  );
}
